import type { ReactNode } from 'react';
import { Icon, type IconProps } from '@/components/ui/icon';
import { cn } from '@/lib/ui/utils';

/**
 * The one capsule used for identity and category tags across the app —
 * "Partner creator", "Featured markets", creator categories. Every tone shares
 * the same height, padding, type size and 12px leading glyph so the pills read
 * as one family wherever they sit next to each other.
 */
export type IdentityPillTone = 'partner' | 'featured' | 'neutral';

const TONE_CLASS: Record<IdentityPillTone, string> = {
  partner: 'border-product-partner-border bg-product-partner-surface text-product-partner-content',
  featured: 'border-feedback-info-border bg-feedback-info-surface text-feedback-info-content',
  neutral: 'border-border-primary bg-background-secondary text-foreground-secondary',
};

export const IDENTITY_PILL_ICON_SIZE = 12;

export function IdentityPill({
  tone,
  icon,
  emoji,
  leading,
  className,
  children,
}: {
  tone: IdentityPillTone;
  /** Hugeicons glyph rendered at the shared 12px size. */
  icon?: IconProps['icon'] | undefined;
  /** Decorative emoji alternative to `icon` (category pills). */
  emoji?: string | undefined;
  /** Custom 12px leading node (brand marks). Ignored when `icon`/`emoji` is set. */
  leading?: ReactNode | undefined;
  className?: string | undefined;
  children: ReactNode;
}): React.ReactNode {
  return (
    <span
      className={cn(
        'inline-flex h-5 shrink-0 items-center gap-1 whitespace-nowrap rounded-pill border px-1.5 text-caption',
        TONE_CLASS[tone],
        className,
      )}
    >
      {icon ? (
        <Icon icon={icon} size={IDENTITY_PILL_ICON_SIZE} />
      ) : emoji ? (
        <span aria-hidden="true" className="text-micro leading-none">
          {emoji}
        </span>
      ) : (
        leading
      )}
      {children}
    </span>
  );
}
